import { forwardRef, useState } from 'react';
import { History } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { Button, ButtonProps } from '../../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { HistoryDrawer } from './HistoryDrawer';

interface HistoryButtonProps extends Omit<ButtonProps, 'children' | 'onClick'> {
  objectType: string;
  objectId: string;
  tooltip?: string;
  showTooltip?: boolean;
}

/**
 * Standardized history icon button that opens the version history drawer.
 */
export const HistoryButton = forwardRef<HTMLButtonElement, HistoryButtonProps>(
  ({ objectType, objectId, tooltip, showTooltip = true, className, ...props }, ref) => {
    const { t } = useTranslation('common');
    const [isOpen, setIsOpen] = useState(false);
    const label = tooltip || t('versioning.viewHistory');

    const button = (
      <Button
        ref={ref}
        variant="ghost"
        size="icon"
        aria-label={label}
        className={cn('hover:bg-primary/10 hover:text-primary', className)}
        onClick={() => setIsOpen(true)}
        {...props}
      >
        <History className="h-4 w-4" />
      </Button>
    );

    return (
      <>
        {showTooltip ? (
          <Tooltip>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent>{label}</TooltipContent>
          </Tooltip>
        ) : (
          button
        )}
        <HistoryDrawer
          open={isOpen}
          onOpenChange={setIsOpen}
          objectType={objectType}
          objectId={objectId}
        />
      </>
    );
  }
);

HistoryButton.displayName = 'HistoryButton';
